// Worker Service for handling heavy computations off the main thread

/* Bump when the worker's output shape or its header contract changes.
 *
 * Both CSV workers are served from the build root under stable, unhashed
 * filenames, so nothing in the URL changes when their contents do. The Docker
 * image's nginx.conf sends no-cache for *.worker.js, but the production deploy
 * is an Azure Web App (UI/azure-pipeline.yml, appType "webApp") whose IIS
 * config -- UI/public/web.config -- sets no Cache-Control at all, so browsers
 * fall back to heuristic freshness and this query is the only thing that gets a
 * user off a stale worker there.
 *
 * A stale csvParser.worker.js is the worst of the four to hold: it is what
 * forward-fills the two-row header and joins group to metric, so an old copy
 * does not fail loudly -- it succeeds against the old contract and hands the UI
 * a wrong-shaped dataset, which is exactly the fabricated result that
 * parseCSVWithWorker refuses to produce on an outright parse failure. */
const CSV_PARSER_WORKER_VERSION = "1";
const RESULTS_PARSER_WORKER_VERSION = "1";

const publicUrl = () => process.env.PUBLIC_URL || "";

/** URL of the worker behind workerService.parseCSV. */
export const csvParserWorkerUrl = () =>
  `${publicUrl()}/csvParser.worker.js?v=${CSV_PARSER_WORKER_VERSION}`;

/** URL of the worker SimulationResults spawns for its preview tables. */
export const resultsParserWorkerUrl = () =>
  `${publicUrl()}/csvWorkerParser.Results.worker.js?v=${RESULTS_PARSER_WORKER_VERSION}`;

class WorkerService {
  constructor() {
    this.workers = new Map();
    this.taskCounter = 0;
  }

  // Parse CSV using Web Worker
  async parseCSV(csvText, options = {}) {
    return new Promise((resolve, reject) => {
      const taskId = ++this.taskCounter;

      try {
        // Create worker
        const worker = new Worker(csvParserWorkerUrl());

        // Set up message handler
        worker.onmessage = (e) => {
          const {
            taskId: responseTaskId,
            success,
            data,
            errors,
            error,
          } = e.data;

          if (responseTaskId === taskId) {
            worker.terminate();
            this.workers.delete(taskId);

            if (success) {
              resolve({ data, errors });
            } else {
              reject(new Error(error));
            }
          }
        };

        // Set up error handler
        worker.onerror = (error) => {
          worker.terminate();
          this.workers.delete(taskId);
          reject(error);
        };

        // Store worker reference
        this.workers.set(taskId, worker);

        // Send task to worker
        worker.postMessage({
          csvText,
          options,
          taskId,
        });

        // Timeout after 30 seconds
        setTimeout(() => {
          if (this.workers.has(taskId)) {
            worker.terminate();
            this.workers.delete(taskId);
            reject(new Error("CSV parsing timeout"));
          }
        }, 30000);
      } catch (error) {
        reject(error);
      }
    });
  }
}

export const workerService = new WorkerService();
